import {
  AnimatedDialog,
  AnimatedDialogContent,
  AnimatedDialogHeader,
  AnimatedDialogTitle,
} from "components/ui/animated-dialog";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { ExternalLink, Sparkles } from "lucide-react";
import React, { useMemo, useState } from "react";
import { graphql, useRefetchableFragment } from "react-relay";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { isRootlyBranded } from "src/utils/brandTheme";

// ---------- Types ----------

interface AnalyticsDataPointType {
  readonly day: string;
  readonly category: string;
  readonly credits: number;
}

interface AnalyticsByDayType {
  readonly totalCredits: number;
  readonly dataPoints: ReadonlyArray<AnalyticsDataPointType>;
}

interface BillingSectionProps {
  organizationBilling: any;
  brandTheme?: string | null;
  isSuspended: boolean;
  suspensionReason: string | null;
  suspendedAt: string | null;
  analyticsByDay: AnalyticsByDayType;
}

// ---------- Helpers ----------

const toISODateKey = (date: Date): string => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatCredits = (value: number): string => {
  return Math.round(value).toLocaleString();
};

// Returns the Monday-based ISO week key for a date, e.g. "2026-W10"
const toWeekKey = (date: Date): string => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
};

const toMonthKey = (date: Date): string => {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
};

interface PeriodConfig {
  windowStart: Date;
  windowEnd: Date;
  toBucketKey: (date: Date) => string;
  allBuckets: string[];
  todayBucket: string;
  formatLabel: (key: string) => string;
}

const getPeriodConfig = (
  period: string | null,
  periodStart: string,
  periodEnd: string,
): PeriodConfig => {
  const windowStart = new Date(periodStart);
  const windowEnd = new Date(periodEnd);
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  if (period === "QUARTERLY") {
    // Weekly buckets
    const buckets: string[] = [];
    const seen = new Set<string>();
    const cur = new Date(windowStart);
    while (cur < windowEnd) {
      const wk = toWeekKey(cur);
      if (!seen.has(wk)) {
        seen.add(wk);
        buckets.push(wk);
      }
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return {
      windowStart,
      windowEnd,
      toBucketKey: toWeekKey,
      allBuckets: buckets,
      todayBucket: toWeekKey(today),
      formatLabel: (key) => {
        const [, wStr] = key.split("-W");
        return `Week ${parseInt(wStr, 10)}`;
      },
    };
  }

  if (period === "ANNUAL") {
    // Monthly buckets
    const buckets: string[] = [];
    const cur = new Date(windowStart);
    while (cur < windowEnd) {
      buckets.push(toMonthKey(cur));
      cur.setUTCMonth(cur.getUTCMonth() + 1);
    }
    return {
      windowStart,
      windowEnd,
      toBucketKey: toMonthKey,
      allBuckets: buckets,
      todayBucket: toMonthKey(today),
      formatLabel: (key) => {
        const [yr, mo] = key.split("-").map(Number);
        return new Date(Date.UTC(yr, mo - 1, 1)).toLocaleDateString("en-US", {
          month: "short",
          timeZone: "UTC",
        });
      },
    };
  }

  // MONTHLY (default) — daily buckets
  const buckets: string[] = [];
  const cur = new Date(windowStart);
  while (cur < windowEnd) {
    buckets.push(toISODateKey(cur));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return {
    windowStart,
    windowEnd,
    toBucketKey: toISODateKey,
    allBuckets: buckets,
    todayBucket: toISODateKey(today),
    formatLabel: (key) => {
      const [yr, mo, dy] = key.split("-").map(Number);
      return new Date(Date.UTC(yr, mo - 1, dy)).toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        timeZone: "UTC",
      });
    },
  };
};

// ---------- Fragment ----------

const billingSectionFragment = graphql`
  fragment BillingSection_organizationBilling on GraphQLOrganizationBilling
  @refetchable(queryName: "BillingSectionRefetchQuery") {
    id
    planType
    marketplaceType
    annualBilling
    effectivePlanName
    isEnterprise
    billingModel
    effectiveCreditsLimit
    awsAccountId
    creditLimit
    creditLimitPeriod
    currentPeriodStart
    currentPeriodEnd
    creditBalance
  }
`;

// ---------- Sub-components ----------

const SUSPENSION_LABELS: Record<string, string> = {
  EVALUATION_ENDED: "Evaluation ended",
  SUBSCRIPTION_ENDED: "Subscription ended",
  CREDIT_LIMIT_REACHED: "Credit limit reached",
};

const BillingPageHeader = React.memo<{
  planName: string;
  isSuspended: boolean;
  suspensionReason: string | null;
  suspendedAt: string | null;
  onManagePlan: () => void;
}>(function BillingPageHeader({
  planName,
  isSuspended,
  suspensionReason,
  suspendedAt,
  onManagePlan,
}) {
  const isEvaluation = suspensionReason === "EVALUATION_ENDED" && suspendedAt && !isSuspended;
  const suspendedAtDate = suspendedAt ? new Date(suspendedAt) : null;

  const renderBadge = () => {
    if (isSuspended && suspensionReason) {
      return (
        <Badge variant="red" size="sm">
          {SUSPENSION_LABELS[suspensionReason] ?? suspensionReason}
        </Badge>
      );
    }
    if (isEvaluation && suspendedAtDate) {
      const diffMs = suspendedAtDate.getTime() - Date.now();
      if (diffMs <= 0) {
        return null;
      }
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      let timeLeft: string;
      if (diffDays > 1) {
        timeLeft = `${diffDays} days`;
      } else {
        const hoursRemaining = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60)));
        timeLeft = `${hoursRemaining} ${hoursRemaining === 1 ? "hour" : "hours"}`;
      }
      return (
        <Badge variant="amber" size="sm">
          Evaluation period — {timeLeft} left
        </Badge>
      );
    }
    return null;
  };

  const formattedExpiry = isEvaluation && suspendedAtDate
    ? suspendedAtDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="hidden md:block mb-6">
      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Subscription</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">{planName} plan</h1>
          {renderBadge()}
        </div>
        <Button variant="outline" onClick={onManagePlan}>
          Manage plan
        </Button>
      </div>
      {formattedExpiry && (
        <p className="text-sm text-muted-foreground mt-1 ml-8">
          Evaluation expires {formattedExpiry}
        </p>
      )}
    </div>
  );
});

const PERIOD_LABELS: Record<string, string> = {
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  ANNUAL: "annual",
};

const getPeriodLabel = (period: string | null, capitalize = false): string => {
  const label = (period && PERIOD_LABELS[period]) || "monthly";
  return capitalize ? label.charAt(0).toUpperCase() + label.slice(1) : label;
};

const UsageSummaryRow = React.memo<{
  creditLimit: number | null;
  creditLimitPeriod: string | null;
}>(function UsageSummaryRow({ creditLimit, creditLimitPeriod }) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs text-muted-foreground mb-1">Included {getPeriodLabel(creditLimitPeriod)} volume</p>
        <p className="text-2xl font-bold text-foreground">
          {creditLimit != null ? `${creditLimit.toLocaleString()} credits` : "Unlimited"}
        </p>
      </CardContent>
    </Card>
  );
});

const BillingUsageChart = React.memo<{
  analyticsByDay: AnalyticsByDayType;
  creditLimit: number | null;
  creditLimitPeriod: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
}>(function BillingUsageChart({
  analyticsByDay,
  creditLimit,
  creditLimitPeriod,
  currentPeriodStart,
  currentPeriodEnd,
}) {
  const [chartType, setChartType] = useState<"daily" | "cumulative">("cumulative");

  const quota = creditLimit ?? 0;
  const config = useMemo(
    () => getPeriodConfig(creditLimitPeriod, currentPeriodStart, currentPeriodEnd),
    [creditLimitPeriod, currentPeriodStart, currentPeriodEnd],
  );

  const chartData = useMemo(() => {
    const { allBuckets, toBucketKey, todayBucket, windowStart, windowEnd } = config;

    // Aggregate actual credits into buckets
    const actualByBucket: Record<string, number> = {};
    analyticsByDay.dataPoints.forEach((dp) => {
      const dpDate = new Date(dp.day);
      if (dpDate < windowStart || dpDate >= windowEnd) return;
      const key = toBucketKey(dpDate);
      actualByBucket[key] = (actualByBucket[key] || 0) + dp.credits;
    });

    // Find the last bucket that has actual data (not just any past bucket)
    const bucketsWithData = allBuckets.filter((b) => b <= todayBucket && actualByBucket[b] > 0);
    const lastDataBucket = bucketsWithData.length > 0 ? bucketsWithData[bucketsWithData.length - 1] : null;

    // Average per-bucket based only on buckets with actual data
    const totalActual = bucketsWithData.reduce((sum, b) => sum + actualByBucket[b], 0);
    const avgPerBucket = bucketsWithData.length > 0 ? totalActual / bucketsWithData.length : 0;

    if (chartType === "cumulative") {
      let cumulative = 0;
      return allBuckets.map((bucket) => {
        if (lastDataBucket && bucket <= lastDataBucket) {
          cumulative += actualByBucket[bucket] || 0;
          return { bucket, actual: cumulative, projected: null as number | null };
        }
        cumulative += avgPerBucket;
        return { bucket, actual: null as number | null, projected: cumulative };
      });
    }

    return allBuckets.map((bucket) => {
      if (lastDataBucket && bucket <= lastDataBucket) {
        return { bucket, actual: actualByBucket[bucket] || 0, projected: null as number | null };
      }
      return { bucket, actual: null as number | null, projected: avgPerBucket };
    });
  }, [analyticsByDay, chartType, config]);

  const tickInterval = chartData.length > 15 ? 2 : 0;

  return (
    <div className="border border-border rounded-lg p-3 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-3">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
            Credit Usage: {config.windowStart.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}
            {" - "}
            {(() => {
              const end = new Date(config.windowEnd);
              end.setUTCDate(end.getUTCDate() - 1);
              return end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
            })()}
          </h3>
          <p className="text-sm text-muted-foreground">
            {creditLimit != null
              ? `${getPeriodLabel(creditLimitPeriod, true)} credit limit: ${creditLimit.toLocaleString()} credits`
              : "Unlimited credits"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={chartType}
            onValueChange={(v) => setChartType(v as "daily" | "cumulative")}
          >
            <SelectTrigger className="w-[180px] bg-popover border-accent text-foreground">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-accent">
              <SelectItem value="cumulative">Type: Cumulative</SelectItem>
              <SelectItem value="daily">Type: Daily</SelectItem>
            </SelectContent>
          </Select>
          <Link
            to="/settings/usage"
            className="text-sm text-primary hover:text-primary/80 flex items-center gap-1 whitespace-nowrap"
          >
            View detailed usage <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>

      <div className="h-[280px] md:h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
            <XAxis
              dataKey="bucket"
              stroke="hsl(var(--chart-axis))"
              tick={{ fill: "hsl(var(--chart-axis))" }}
              tickLine={{ stroke: "hsl(var(--chart-axis))" }}
              tickFormatter={config.formatLabel}
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={tickInterval}
            />
            <YAxis
              stroke="hsl(var(--chart-axis))"
              tick={{ fill: "hsl(var(--chart-axis))" }}
              tickLine={{ stroke: "hsl(var(--chart-axis))" }}
              fontSize={12}
              tickFormatter={formatCredits}
            />
            <RechartsTooltip
              content={({ active, payload, label }: any) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-accent border border-border rounded-md p-3 shadow-lg">
                    <p className="text-foreground font-medium mb-2">{config.formatLabel(label)}</p>
                    {payload.map(
                      (entry: any, index: number) =>
                        entry.value != null && (
                          <div key={index} className="flex items-center gap-2 text-sm">
                            <div
                              className="w-3 h-3 rounded-sm"
                              style={{ backgroundColor: entry.color }}
                            />
                            <span className="text-muted-foreground">
                              {entry.dataKey === "actual" ? "Actual" : "Projected"}:{" "}
                              {entry.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}{" "}
                              credits
                            </span>
                          </div>
                        )
                    )}
                  </div>
                );
              }}
            />
            <Bar dataKey="actual" fill="#10b981" name="actual" radius={[2, 2, 0, 0]} />
            <Bar dataKey="projected" fill="#8b5cf6" name="projected" radius={[2, 2, 0, 0]} />
            {quota > 0 && chartType === "cumulative" && (
              <ReferenceLine
                y={quota}
                stroke="#f59e0b"
                strokeDasharray="6 4"
                label={{
                  value: `Plan Quota (${formatCredits(quota)})`,
                  position: "insideLeft",
                  fill: "#f59e0b",
                  fontSize: 12,
                  dy: -10,
                }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-6 mt-3 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-[#10b981]" />
          <span>Actual usage</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-[#8b5cf6]" />
          <span>Projected usage</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0 border-t-2 border-dashed border-[#f59e0b]" />
          <span>Credit limit</span>
        </div>
      </div>
    </div>
  );
});

// ---------- Main component ----------

export default function BillingSection({
  organizationBilling,
  brandTheme,
  isSuspended,
  suspensionReason,
  suspendedAt,
  analyticsByDay,
}: BillingSectionProps) {
  const [billingData] = useRefetchableFragment(billingSectionFragment, organizationBilling);
  const [showManagePlan, setShowManagePlan] = useState(false);

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 max-w-7xl mx-auto">
      <BillingPageHeader
        planName={billingData.effectivePlanName}
        isSuspended={isSuspended}
        suspensionReason={suspensionReason}
        suspendedAt={suspendedAt}
        onManagePlan={() => setShowManagePlan(true)}
      />

      <div className="mb-4">
        <UsageSummaryRow
          creditLimit={billingData.creditLimit ?? null}
          creditLimitPeriod={billingData.creditLimitPeriod ?? null}
        />
      </div>

      <BillingUsageChart
        analyticsByDay={analyticsByDay}
        creditLimit={billingData.creditLimit ?? null}
        creditLimitPeriod={billingData.creditLimitPeriod ?? null}
        currentPeriodStart={billingData.currentPeriodStart}
        currentPeriodEnd={billingData.currentPeriodEnd}
      />

      {/* Manage plan dialog */}
      <AnimatedDialog open={showManagePlan} onOpenChange={setShowManagePlan}>
        <AnimatedDialogContent className="bg-popover border-accent text-foreground">
          <AnimatedDialogHeader>
            <AnimatedDialogTitle>Manage Plan</AnimatedDialogTitle>
          </AnimatedDialogHeader>
          <p className="text-muted-foreground text-sm mt-4">
            Please contact{" "}
            {isRootlyBranded(brandTheme) ? "support@rootly.com" : "support@tierzero.ai"}{" "}
            or send us a Slack message for plan or limit changes.
          </p>
        </AnimatedDialogContent>
      </AnimatedDialog>
    </div>
  );
}
